import { createContext, useContext, useState, type ReactNode } from "react";

import { createBackend, type Backend } from "../backend";

const BackendContext = createContext<Backend | null>(null);

interface BackendProviderProps {
  children: ReactNode;
  /** Injection d'un backend prêt (tests). Sinon il est créé depuis l'environnement. */
  backend?: Backend;
}

/**
 * Crée le backend une seule fois pour la vie de la page.
 *
 *  - `backend` injecté (tests) → utilisé tel quel (typiquement un
 *    `createMockedBackend()` — voir `src/mocks/`, qui ne sert plus qu'aux tests).
 *  - sinon : `createBackend()` (API réelle), synchrone, en développement comme
 *    en production — aucun commutateur de mode démonstration.
 *
 * On ne dispose pas le backend au démontage : sous StrictMode le cycle
 * montage/démontage/remontage réutiliserait une instance disposée.
 */
export function BackendProvider({ children, backend }: BackendProviderProps) {
  const [created] = useState<Backend>(() => backend ?? createBackend());

  return <BackendContext.Provider value={created}>{children}</BackendContext.Provider>;
}

export function useBackend(): Backend {
  const value = useContext(BackendContext);
  if (!value) throw new Error("useBackend doit être utilisé sous <BackendProvider>");
  return value;
}
